// services/adminApi.js
// Centralized Axios HTTP client for admin requests with HttpOnly cookie support & auto-refresh.
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
const ADMIN_LOGIN_PATH = "/admin/login";

const adminApi = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

export function handleAdminAuthFailure() {
  if (typeof window !== "undefined") {
    localStorage.removeItem("jb_admin");
    localStorage.removeItem("jb_admin_token");
    localStorage.removeItem("jb_admin_refresh_token");
    if (!window.location.pathname.startsWith(ADMIN_LOGIN_PATH)) {
      window.location.href = ADMIN_LOGIN_PATH;
    }
  }
}

// Admin response interceptor with automatic token refresh queuing
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Avoid infinite loop on admin auth endpoints
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/api/admin/refresh") &&
      !originalRequest.url?.includes("/api/admin/login")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => adminApi(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(
          `${API_BASE}/api/admin/refresh`,
          {},
          { withCredentials: true },
        );
        processQueue(null);
        return adminApi(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr);
        handleAdminAuthFailure();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default adminApi;
